import { useCallback, useState } from 'react';
import { apiFetch } from '@/lib/api-client';
import { captureUploadPutFailure } from './photo-materialize';

/**
 * 5a Commit 3 · signed-uploads wire hook.
 *
 * Runs the two-hop upload for a batch of File objects:
 *
 *   1. POST /api/book/photos/signed-uploads
 *      Payload: `{ photos: [{ contentType }] }`
 *      Response: `{ photos: [{ key, url }] }` (same order as request)
 *   2. PUT each File to its signed URL (all in parallel), returning
 *      the keys array in payload order.
 *
 * Auth: JWT-gated per `src/book/photos/photos.controller.ts:33`. The
 * Light-Gate registration must have completed before the first call
 * here (`useAuthStore.accessToken` must be non-null); `apiFetch`
 * attaches the header. Calling this before auth is a client-side bug
 * that will surface as a 401 from the endpoint.
 *
 * ═══ PROGRESS ══════════════════════════════════════════════════════
 *
 * Sebastian ruling 2026-08-06 (Commit 3 constraints): sixteen photos
 * take a moment, that needs visible progress. This hook exposes a
 * `{ total, completed }` counter that ticks as each PUT resolves —
 * enough for a "N von M hochgeladen" line, without XHR-per-file
 * byte-level progress. Progress is null while idle; resets on every
 * `upload()` call.
 *
 * ═══ ORDER + FAILURE ═══════════════════════════════════════════════
 *
 * `keys[i]` corresponds to `files[i]`. A failure at either hop (batch
 * signed-URL request OR any individual PUT) rejects the whole
 * `upload()` promise. Already-succeeded PUTs before a mid-batch
 * failure are ORPHANS by design — `[book-photos-orphan-sweep]` owns
 * the lifecycle rule (backend GCS lifecycle on `book-uploads/*`).
 * The caller re-tries by calling `upload(files)` again with the same
 * File array; fresh signed URLs get minted for every attempt.
 */

export interface PhotoUploadProgress {
  readonly total: number;
  readonly completed: number;
}

interface SignedUploadsResponse {
  readonly photos: readonly {
    readonly key: string;
    readonly url: string;
    /**
     * Byte cap the URL is signed with. The GCS v4 signed URL binds
     * this into the `x-goog-content-length-range` extension header,
     * so the browser MUST send `x-goog-content-length-range: 0,${maxBytes}`
     * on the PUT or GCS rejects with 400 SignatureDoesNotMatch.
     * Threaded from the backend (`src/book/photos/photos.service.ts`
     * MintedUploadUrl.maxBytes) so the frontend doesn't have to know
     * the constant separately.
     */
    readonly maxBytes: number;
  }[];
}

export interface UsePhotoUpload {
  readonly upload: (files: readonly File[]) => Promise<readonly string[]>;
  readonly progress: PhotoUploadProgress | null;
  readonly reset: () => void;
}

export function usePhotoUpload(): UsePhotoUpload {
  const [progress, setProgress] = useState<PhotoUploadProgress | null>(null);

  const upload = useCallback(async (files: readonly File[]): Promise<readonly string[]> => {
    if (files.length === 0) return [];
    setProgress({ total: files.length, completed: 0 });

    const resp = await apiFetch<SignedUploadsResponse>('/api/book/photos/signed-uploads', {
      method: 'POST',
      body: { photos: files.map((f) => ({ contentType: f.type })) },
    });

    if (resp.photos.length !== files.length) {
      throw new Error(
        `usePhotoUpload: backend returned ${resp.photos.length} URLs for ${files.length} files`,
      );
    }

    const keys: string[] = new Array<string>(files.length);
    let completed = 0;

    await Promise.all(
      resp.photos.map(async (entry, i) => {
        const file = files[i]!;
        let putResp: Response;
        try {
          putResp = await fetch(entry.url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
              // GCS v4 signed URLs bind extension headers into the
              // signature; the browser MUST send the exact string the
              // URL was signed with or GCS returns 400
              // SignatureDoesNotMatch. `maxBytes` comes from the backend
              // response (mirrors `MAX_PHOTO_BYTES` in
              // src/book/photos/photos.constants.ts, currently 10 MiB).
              // A malicious client tampering with the range value falls
              // over on the signature check — not a bytes-through-the-
              // door bypass (Sebastian ruling 2026-09-07: keep the byte
              // cap where it's already enforced).
              'x-goog-content-length-range': `0,${entry.maxBytes}`,
            },
          });
        } catch (err) {
          // Pre-response network failure (CORS, DNS, offline, lazy
          // content-URI resolve throw). The signed-URL `entry.url`
          // carries a GCS signature; we tag with safe metadata only
          // — no file name, no URL, no bytes.
          captureUploadPutFailure({
            httpStatus: 'network',
            fileSize: file.size,
            fileType: file.type,
            index: i,
            total: files.length,
          });
          throw err;
        }
        if (!putResp.ok) {
          captureUploadPutFailure({
            httpStatus: putResp.status,
            fileSize: file.size,
            fileType: file.type,
            index: i,
            total: files.length,
          });
          throw new Error(
            `usePhotoUpload: PUT to signed URL failed with ${putResp.status} at index ${i}`,
          );
        }
        keys[i] = entry.key;
        completed += 1;
        setProgress({ total: files.length, completed });
      }),
    );

    return keys;
  }, []);

  const reset = useCallback(() => setProgress(null), []);

  return { upload, progress, reset };
}
